/**
 * Scan Engine Context
 *
 * Provides the unified scan engine to the component tree.
 * Place <ScanEngineProvider> inside BrowserRouter so the engine
 * can use useLocation for route-change resets.
 *
 * Renders a fixed-position overlay for the scan focus ring,
 * driven entirely by React state (no DOM classList manipulation).
 */

import React, { createContext, useContext, useMemo, useState, useLayoutEffect, useEffect, useRef, useCallback } from 'react';
import { useScanEngine, ScanEngineState, FocusOverlay } from '@/hooks/useScanEngine';
import { useTranslation } from '@/hooks/useTranslation';

interface ScanEngineContextValue {
  isActive: boolean;
  refreshItems: () => void;
}

const ScanEngineContext = createContext<ScanEngineContextValue>({
  isActive: false,
  refreshItems: () => {},
});

// ── Focus Overlay Component ────────────────────────────────────────────────

interface OverlayRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

function ScanFocusOverlay({ overlay }: { overlay: FocusOverlay | null }) {
  const [rects, setRects] = useState<OverlayRect[]>([]);
  const rafRef = useRef<number>(0);

  // Read rects once synchronously when overlay changes
  useLayoutEffect(() => {
    if (!overlay || overlay.elements.length === 0) {
      setRects([]);
      return;
    }

    const newRects = overlay.elements
      .map(el => {
        const r = el.getBoundingClientRect();
        return { left: r.left, top: r.top, width: r.width, height: r.height };
      })
      .filter(r => r.width > 0 && r.height > 0);

    setRects(newRects);
  }, [overlay]);

  // rAF loop: re-read rects every frame so overlay follows scrolling/animations
  useEffect(() => {
    if (!overlay || overlay.elements.length === 0) return;

    const tick = () => {
      const newRects = overlay.elements
        .map(el => {
          const r = el.getBoundingClientRect();
          return { left: r.left, top: r.top, width: r.width, height: r.height };
        })
        .filter(r => r.width > 0 && r.height > 0);

      setRects(prev => {
        // Only update if rects actually changed to avoid unnecessary re-renders
        if (prev.length !== newRects.length) return newRects;
        for (let i = 0; i < prev.length; i++) {
          if (prev[i].left !== newRects[i].left || prev[i].top !== newRects[i].top ||
              prev[i].width !== newRects[i].width || prev[i].height !== newRects[i].height) {
            return newRects;
          }
        }
        return prev;
      });

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [overlay]);

  if (rects.length === 0) return null;

  const isItem = overlay?.type === 'item';

  return (
    <>
      {rects.map((rect, i) => (
        <div
          key={i}
          className={isItem ? 'scan-focus-overlay' : 'scan-row-focus-overlay'}
          style={{
            position: 'fixed',
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
            pointerEvents: 'none',
            zIndex: 9999,
            borderRadius: '0.75rem',
          }}
        />
      ))}
    </>
  );
}

// ── Provider ───────────────────────────────────────────────────────────────

export function ScanEngineProvider({ children }: { children: React.ReactNode }) {
  const { isRTL } = useTranslation();
  const engine = useScanEngine(isRTL);

  // Memoize context value to prevent child re-renders on focus changes
  const contextValue = useMemo(() => ({
    isActive: engine.isActive,
    refreshItems: engine.refreshItems,
  }), [engine.isActive, engine.refreshItems]);

  return (
    <ScanEngineContext.Provider value={contextValue}>
      {children}
      <ScanFocusOverlay overlay={engine.focusOverlay} />
    </ScanEngineContext.Provider>
  );
}

/** Read the scan engine state. Safe to call even when the engine is dormant. */
export function useScanEngineContext(): ScanEngineContextValue {
  return useContext(ScanEngineContext);
}
